import { LinearGradient } from 'expo-linear-gradient'
import { Circle, Text, ITextProps, Image, Row, Box } from 'native-base'
import React, { useEffect, useMemo, FunctionComponent, useRef } from 'react'
// NativeBase Input is not animating the value
import { TextInput, StyleSheet } from 'react-native'
import Animated, {
  useAnimatedProps,
  useDerivedValue,
  useSharedValue,
  Easing,
  withTiming,
} from 'react-native-reanimated'

import { theme } from '~constants'
import { useDeviceInfo, useTheme, useTranslation } from '~hooks'
import { Images } from '~images'
import { PointsIndicatorProps } from '~types/PointsIndicator'
import { convertCoinsToDollars } from '~utils'

Animated.addWhitelistedNativeProps({ text: true })

const AnimatedTextInput = Animated.createAnimatedComponent(
  TextInput as unknown as FunctionComponent
)

export const AnimatedPointsIndicator = ({
  animated = false,
  balance,
  variant = 'primary',
  withText = true,
}: PointsIndicatorProps) => {
  const { fontWeights, fontSizes, colors } = useTheme()
  const { isAndroid } = useDeviceInfo()

  const currentBalance = useRef(+(+balance).toFixed(2))

  const counterProgress = useSharedValue(+(+balance).toFixed(2))

  useEffect(() => {
    if (+balance !== currentBalance.current && animated) {
      counterProgress.value = currentBalance.current
      counterProgress.value = withTiming(
        +balance,
        {
          duration: 1000,
          easing: Easing.linear,
        },
        (finished) => {
          if (finished) {
            currentBalance.current = +balance
          }
        }
      )
    } else if (balance) {
      currentBalance.current = +balance
    }
  }, [animated, balance, counterProgress])

  const text = useDerivedValue<string>(
    () => `${Math.floor(counterProgress.value)}`,
    [counterProgress]
  )

  const fontColor = useMemo(
    (): ITextProps['color'] =>
      (variant === 'primary' ? theme.colors.actionBlue : theme.colors.white) as string,
    [variant]
  )

  const logoImageProps = useMemo(
    () => ({
      h: withText ? '8' : '6',
      w: withText ? '8' : '6',
      padding: withText ? '2' : '1.5',
      ...(withText && { bg: 'rgba(255,255,255, 0.2)' }),
    }),
    [withText]
  )

  const pointsValue = useMemo(
    (): string => (animated ? text?.value : currentBalance.current.toString()),
    //currentBalance need to be in array dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [animated, text?.value, currentBalance?.current]
  )

  const inputStyle = useMemo(
    (): {
      color: string
      fontFamily: string
      fontSize: number
      fontWeight: string
    } => ({
      color: fontColor as string,
      fontFamily: 'Roboto-Bold',
      fontSize: fontSizes[withText ? '3xl' : '2xl'],
      fontWeight: fontWeights?.extrabold?.toString?.() || '700',
    }),
    [fontSizes, fontWeights, withText, fontColor]
  )
  const animatedProps = useAnimatedProps((): { text: string } => {
    return {
      text: pointsValue,
    }
  })

  const gradientStyle = useMemo(
    () => ({
      ...StyleSheet.absoluteFillObject,
      borderRadius: 99,
    }),
    []
  )

  return (
    <Row alignItems={'center'} flexShrink={1}>
      <AnimatedTextInput
        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore  Reanimated doesnt accept below val as prop
        editable={false}
        style={inputStyle}
        underlineColorAndroid="transparent"
        value={pointsValue}
        {...(animated && { animatedProps })}
      />
      <Circle {...logoImageProps} ml="2" overflow="hidden">
        {!withText ? (
          <LinearGradient
            colors={[colors['offBlue'], colors['actionBlue']]}
            style={gradientStyle}
            start={{ x: 0, y: 0.1 }}
            end={{ x: 0.7, y: 1 }}
          />
        ) : null}
        <Image
          alt="logo"
          h="100%"
          ignoreFallback
          resizeMode="contain"
          source={Images.SYGNET}
          w="100%"
        />
      </Circle>
      {withText ? (
        <Text
          alignSelf="flex-end"
          color={fontColor}
          fontFamily="mono"
          fontSize="md"
          fontWeight="700"
          mb={isAndroid ? '1.5' : '0.5'}
          ml="1"
        >
          ${convertCoinsToDollars(+balance)}
        </Text>
      ) : null}
    </Row>
  )
}

export const PointsIndicator = ({
  balance,
  variant = 'primary',
  withText = true,
}: PointsIndicatorProps) => {
  const { colors } = useTheme()
  const { isAndroid } = useDeviceInfo()
  const { t } = useTranslation()
  const fontColor = useMemo(
    (): ITextProps['color'] =>
      (variant === 'primary' ? theme.colors.actionBlue : theme.colors.white) as string,
    [variant]
  )

  const logoImageProps = useMemo(
    () => ({
      h: withText ? '8' : '6',
      w: withText ? '8' : '6',
      padding: withText ? '2' : '1.5',
      ...(withText && { bg: 'rgba(255,255,255, 0.2)' }),
    }),
    [withText]
  )

  const gradientStyle = useMemo(
    () => ({
      ...StyleSheet.absoluteFillObject,
      borderRadius: 99,
    }),
    []
  )

  return (
    <Row alignItems={'center'} flexShrink={1}>
      <Box alignItems={'center'} justifyContent={'center'} flexDirection={'column'}>
        <Box alignItems={'center'} justifyContent={'center'} flexDirection={'row'}>
          <Text color={fontColor} fontSize={withText ? '3xl' : '2xl'} style={styles.pointsText}>
            {balance}
          </Text>
          <Circle {...logoImageProps} ml="2" overflow="hidden">
            {!withText ? (
              <LinearGradient
                colors={[colors['offBlue'], colors['actionBlue']]}
                style={gradientStyle}
                start={{ x: 0, y: 0.1 }}
                end={{ x: 0.7, y: 1 }}
              />
            ) : null}
            <Image
              alt="logo"
              h="100%"
              ignoreFallback
              resizeMode="contain"
              source={Images.SYGNET}
              w="100%"
            />
          </Circle>
        </Box>

        <Text color={fontColor} opacity="0.9" alignSelf={'flex-start'} ml={1} letterSpacing={1}>
          {t('common.balance')}
        </Text>
      </Box>

      {withText ? (
        <Text
          alignSelf="flex-end"
          color={fontColor}
          fontFamily="mono"
          fontSize="md"
          fontWeight="700"
          mb={isAndroid ? '1.5' : '0.5'}
          ml="1"
          lineHeight="3xl"
          textAlign="center"
        >
          ${convertCoinsToDollars(+balance)}
        </Text>
      ) : null}
    </Row>
  )
}

const styles = StyleSheet.create({
  pointsText: {
    fontFamily: 'Roboto-Bold',
  },
})
